/**
 * The review page: every Shorts board with an edit job, and, per board, each
 * Short's state with the last phase's video to watch, notes for the editor
 * and the verdict. A verdict queues the next phase on a machine; approving
 * the final marks the Short cut. Viewers watch; editors and admins decide.
 */
import { useEffect, useState } from 'react';
import { ApiError, type Claims, type ReviewJob, type ReviewShort, type WebApi } from '../api.js';
import { Button, Icon, Note, TextField } from '../ui/index.js';

/** A Short's state in the owner's words. */
export function shortLabel(s: Pick<ReviewShort, 'state' | 'phase'>): string {
  switch (s.state) {
    case 'pending': return s.phase ? `Waiting for a machine: ${s.phase}` : 'Waiting for a machine';
    case 'animatic-running': return 'Making the animatic';
    case 'fix-animatic-running': return 'Applying animatic notes';
    case 'final-running': return 'Rendering the final';
    case 'fix-final-running': return 'Applying final notes';
    case 'animatic-in-review': return 'Animatic ready to review';
    case 'final-in-review': return 'Final ready to review';
    case 'cut': return 'Cut';
    case 'skipped': return 'Already cut';
    case 'failed': return 'Failed';
    default: return s.state;
  }
}
/** The chip beside a Short or a board: one word, its tone from the design tokens. */
function Chip({ state }: { state: string }) {
  const [tone, label] = state === 'cut' || state === 'skipped' || state === 'done' ? ['approved', 'Cut'] : state.endsWith('-in-review') ? ['released', 'In review'] : state === 'failed' ? ['warning', 'Failed'] : state.endsWith('-running') || state === 'working' ? ['neutral', 'Working'] : ['neutral', 'Queued'];
  return <span className={`rv-chip rv-chip--${tone}`}>{label}</span>;
}
const errorOf = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
const when = (iso: string) => new Date(iso).toLocaleString();

export function ReviewPage({ api, role, boardId, onOpen }: { api: WebApi; role: Claims['role']; boardId?: string; onOpen: (boardId: string | null) => void }) {
  return boardId ? <BoardReview api={api} role={role} boardId={boardId} onBack={() => onOpen(null)} /> : <ReviewList api={api} onOpen={onOpen} />;
}

function ReviewList({ api, onOpen }: { api: WebApi; onOpen: (boardId: string) => void }) {
  const [jobs, setJobs] = useState<ReviewJob[] | null>(null); const [error, setError] = useState<string | null>(null);
  useEffect(() => { let on = true; api.review.list().then((j) => { if (on) setJobs(j); }, (e) => { if (on) setError(errorOf(e)); }); return () => { on = false; }; }, [api]);
  const summary = (j: ReviewJob) => {
    const waiting = j.shorts.filter((s) => s.state.endsWith('-in-review')).length;
    const cut = j.shorts.filter((s) => s.state === 'cut' || s.state === 'skipped').length;
    return waiting ? `${waiting} ready to review` : cut === j.shorts.length ? 'All cut' : `${cut} of ${j.shorts.length} cut`;
  };
  return (
    <section className="rv-page">
      <header className="rv-page__head"><h1 className="rv-title">Review</h1></header>
      {error ? <Note tone="danger">{error}</Note> : null}
      {jobs === null ? <p className="rv-body rv-muted">Loading…</p> : jobs.length === 0 ? <p className="rv-body rv-muted">Nothing to review. Boards in Editing appear here.</p> : (
        <ul className="rv-list" aria-label="Boards in editing">
          {jobs.map((j) => (
            <li key={j.boardId} className="rv-list__row">
              <button type="button" className="rv-list__main" onClick={() => onOpen(j.boardId)}>
                <span className="rv-list__title">{j.boardName}</span>
                <span className="rv-list__meta">{summary(j)} · {j.shorts.length === 1 ? '1 Short' : `${j.shorts.length} Shorts`} · ${j.costUsd.toFixed(2)}</span>
              </button>
              <Chip state={j.shorts.some((s) => s.state.endsWith('-in-review')) ? 'animatic-in-review' : j.shorts.some((s) => s.state.endsWith('-running')) ? 'working' : j.state} />
            </li>))}
        </ul>)}
    </section>
  );
}

function BoardReview({ api, role, boardId, onBack }: { api: WebApi; role: Claims['role']; boardId: string; onBack: () => void }) {
  const canDecide = role !== 'viewer';
  const [data, setData] = useState<{ state: string; shorts: ReviewShort[]; costUsd: number } | null>(null);
  const [error, setError] = useState<string | null>(null); const [selected, setSelected] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = async () => { try { const d = await api.review.shorts(boardId); setData(d); setSelected((s) => s ?? d.shorts.find((x) => x.state.endsWith('-in-review'))?.slot ?? d.shorts[0]?.slot ?? null); } catch (e) { setError(errorOf(e)); } };
  useEffect(() => { void load(); }, [boardId]);
  // While a machine is on it, the page follows along.
  const working = data?.shorts.some((s) => s.state === 'pending' || s.state.endsWith('-running')) ?? false;
  useEffect(() => { if (!working) return; const t = setInterval(() => { void load(); }, 10_000); return () => clearInterval(t); }, [working, boardId]);
  const short = data?.shorts.find((s) => s.slot === selected) ?? null;
  return (
    <section className="rv-page">
      <header className="rv-page__head">
        <Button variant="ghost" icon="chevron-left" onClick={onBack}>Review</Button>
        <h1 className="rv-title">{data ? `${data.shorts.length === 1 ? 'Short' : 'Shorts'} · $${data.costUsd.toFixed(2)}` : 'Shorts'}</h1>
      </header>
      {error ? <Note tone="danger">{error}</Note> : null}
      {notice ? <p className="rv-body rv-muted" role="status">{notice}</p> : null}
      {data === null ? <p className="rv-body rv-muted">Loading…</p> : (
        <div className="rv-review">
          <ul className="rv-list rv-review__shorts" aria-label="Shorts">
            {data.shorts.map((s) => (
              <li key={s.slot} className="rv-list__row">
                <button type="button" className="rv-list__main" aria-current={s.slot === selected ? 'true' : undefined} onClick={() => { setSelected(s.slot); setNotice(null); }}>
                  <span className="rv-list__title">Short {s.slot}{s.title ? ` · ${s.title}` : ''}</span>
                  <span className="rv-list__meta">{shortLabel(s)}{s.reviewVersion ? ` · v${s.reviewVersion}` : ''}</span>
                </button>
                <Chip state={s.state} />
              </li>))}
          </ul>
          {short ? <ShortReview key={`${short.slot}:${short.state}:${short.reviewVersion ?? 0}`} api={api} boardId={boardId} short={short} canDecide={canDecide} onDone={async (m) => { setNotice(m); await load(); }} /> : null}
        </div>)}
    </section>
  );
}

function ShortReview({ api, boardId, short, canDecide, onDone }: { api: WebApi; boardId: string; short: ReviewShort; canDecide: boolean; onDone: (message: string) => Promise<void> }) {
  const [notes, setNotes] = useState(''); const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [video, setVideo] = useState<{ url: string } | { local: string } | null>(null);
  const review = short.review;
  useEffect(() => {
    let on = true;
    if (!review) { setVideo(null); return; }
    if (!review.bucketKey) { setVideo({ local: review.file }); return; }
    api.media.objectUrl(review.bucketKey).then((url) => { if (on) setVideo({ url }); }, () => { if (on) setVideo({ local: review.file }); });
    return () => { on = false; };
  }, [api, review?.bucketKey, review?.file]);
  const inReview = short.state.endsWith('-in-review');
  const isFinal = short.state === 'final-in-review';
  const noteList = notes.split('\n').map((t) => t.trim()).filter(Boolean).map((text) => ({ text }));
  const decide = async (decision: 'approved' | 'changes_requested' | 'retry', message: string) => {
    if (decision === 'approved' && isFinal && !window.confirm(`Approve the final of Short ${short.slot}? It is marked cut.`)) return;
    setError(null); setBusy(decision);
    try { await api.review.decide(boardId, short.slot, { decision, ...(noteList.length ? { notes: noteList } : {}) }); setNotes(''); await onDone(message); }
    catch (e) { setError(errorOf(e)); }
    finally { setBusy(null); }
  };
  return (
    <article className="rv-review__detail" aria-label={`Short ${short.slot}`}>
      <header className="rv-row rv-row--between">
        <h2 className="rv-heading">Short {short.slot}{short.title ? ` · ${short.title}` : ''}</h2>
        <span className="rv-body rv-muted">{shortLabel(short)}{short.reviewVersion ? ` · v${short.reviewVersion}` : ''} · ${short.costUsd.toFixed(2)}</span>
      </header>
      {short.lastError ? <Note tone="danger">{short.lastError}</Note> : null}
      {review ? (
        <div className="rv-review__media">
          {video && 'url' in video ? <video className="rv-review__video" controls preload="none" src={video.url} aria-label={`${review.phase} of Short ${short.slot}`} /> : (
            <div className="rv-review__video rv-review__video--empty"><Icon name="monitor" size={20} /><span className="rv-body rv-muted">{video ? `On the machine: ${video.local}` : 'Loading the video…'}</span></div>)}
          <p className="rv-list__meta">{review.phase} · {(review.bytes / 1048576).toFixed(1)} MB · {when(review.at)}{review.notes ? ` · ${review.notes}` : ''}</p>
          {review.questions ? <details className="rv-review__questions"><summary className="rv-body">Questions from the editor</summary><pre className="rv-review__pre">{review.questions}</pre></details> : null}
        </div>) : <p className="rv-body rv-muted">Nothing to watch yet.</p>}
      {canDecide && (inReview || short.state === 'failed') ? (
        <form className="rv-stack" onSubmit={(e) => { e.preventDefault(); }}>
          {inReview ? <TextField multiline label="Notes for the editor" hint="One note per line. Add [full animation] to skip to the final." value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} /> : null}
          <div className="rv-row">
            {inReview ? <Button variant="primary" icon="check" busy={busy === 'approved'} disabled={busy !== null} onClick={() => void decide('approved', isFinal ? 'Marked cut.' : 'Final queued.')}>{isFinal ? 'Approve final' : 'Approve animatic'}</Button> : null}
            {inReview ? <Button icon="message-square" busy={busy === 'changes_requested'} disabled={busy !== null || noteList.length === 0} onClick={() => void decide('changes_requested', 'Notes sent; the fix is queued.')}>Request changes</Button> : null}
            {short.state === 'failed' ? <Button variant="primary" icon="play" busy={busy === 'retry'} disabled={busy !== null} onClick={() => void decide('retry', 'Queued again.')}>Retry</Button> : null}
          </div>
          {error ? <Note tone="danger">{error}</Note> : null}
        </form>) : null}
      {short.steps.length ? (
        <ol className="rv-review__steps" aria-label="History">
          {short.steps.slice(-8).reverse().map((st, i) => <li key={`${st.at}-${i}`} className="rv-list__meta">{when(st.at)} · {st.state === 'pending' ? 'Queued' : shortLabel({ state: st.state, phase: null })}</li>)}
        </ol>) : null}
    </article>
  );
}
